import { Vec } from '@tldraw/vec'
import {
  SessionType,
  TDShape,
  TDBinding,
  TldrawPatch,
  WhiteboardStore, TldrawCommand, PagePartial
} from '@modules/whiteboard'
import {BaseSession} from '@modules/whiteboard/sessions/BaseSession'
import { action, computed, observable } from '@api'

export class EraseSession extends BaseSession {
  type = SessionType.Draw
  @observable isLocked?: boolean
  @observable lockedDirection?: 'horizontal' | 'vertical'
  @observable erasedShapesSet = new Set<TDShape>()
  @observable erasedBindingsSet = new Set<TDBinding>()
  @observable initialSelectedShapes: TDShape[]
  @observable erasableShapes: TDShape[]
  @observable prevPoint: number[]

  @computed get erasedShapes() { return Array.from(this.erasedShapesSet.values())}
  @computed get erasedBindings() { return Array.from(this.erasedBindingsSet.values())}
  @computed get erasedShapeIdsSet() { return new Set<string>(this.erasedShapes.map(s => s.id))}

  constructor(app: WhiteboardStore) {
    super(app)
    this.prevPoint = [...app.originPoint]
    this.initialSelectedShapes = app.selectedShapes.slice()
    this.erasableShapes = app.unlockedShapes.slice()
  }

  @action start = (): TldrawPatch | undefined => {
    this.status = 'creating'
    return void null
  }

  @action update = (): TldrawPatch | undefined => {
    const { app: { page, shiftKey, originPoint, currentPoint, shapeUtilFor, getShape }, erasedShapesSet, erasedBindingsSet, prevPoint} = this

    if (shiftKey) {
      if (!this.isLocked && Vec.dist(originPoint, currentPoint) > 4) {
        // If we're locking before knowing what direction we're in, set it
        // early based on the bigger dimension.
        if (!this.lockedDirection) {
          const delta = Vec.sub(currentPoint, originPoint)
          this.lockedDirection = delta[0] > delta[1] ? 'horizontal' : 'vertical'
        }

        this.isLocked = true
      }
    } else if (this.isLocked) {
      this.isLocked = false
    }

    if (this.isLocked) {
      if (this.lockedDirection === 'vertical') {
        currentPoint[0] = originPoint[0]
      } else {
        currentPoint[1] = originPoint[1]
      }
    }

    const newPoint = Vec.toFixed(Vec.add(originPoint, Vec.sub(currentPoint, originPoint)))

    const deletedShapeIds = new Set<string>([])

    for (const shape of this.erasableShapes) {
      if (this.erasedShapesSet.has(shape)) continue

      if (shapeUtilFor(shape).hitTestLineSegment(shape, prevPoint, newPoint)) {
        this.erasedShapesSet.add(shape)
        deletedShapeIds.add(shape.id);

        // Delete children
        (shape.children ?? []).forEach(childId => {
          const child = getShape(childId)
          erasedShapesSet.add(child)
          deletedShapeIds.add(childId)
          child.isGhost = true
        })
      }
    }

    // Erase bindings that reference deleted shapes
    Object.values(page.bindings).forEach((binding) => {
      for (const id of [binding.toId, binding.fromId]) {
        if (deletedShapeIds.has(id)) {
          erasedBindingsSet.add(binding)
        }
      }
    })

    const erasedShapes = Array.from(this.erasedShapesSet.values())

    this.prevPoint = newPoint
    erasedShapes.forEach(s => s.isGhost = true)

    return undefined
  }

  @action cancel = (): TldrawPatch | undefined => {
    const { app: {myPageState}, initialSelectedShapes, erasedShapes } = this

    erasedShapes.forEach(s => s.isGhost = false)
    myPageState.selectedIds = initialSelectedShapes.map(s => s.id)

    return undefined
  }

  @computed get erasedBindingIds() {
    return this.erasedBindings.map(b => b.id)
  }

  @computed get erasedBindingIdsSet() {
    return new Set<string>(this.erasedBindingIds)
  }

  @action complete = (): TldrawPatch | TldrawCommand | undefined => {
    const { initialSelectedShapes, app: {page: {bindings, shapes}, myPageState}, erasedShapes, erasedShapeIdsSet, erasedShapesSet, erasedBindingIds, erasedBindingIdsSet} = this

    erasedBindingIds.forEach(id => delete bindings[id])
    erasedShapes.forEach(s => delete shapes[s.id])
    myPageState.selectedIds = initialSelectedShapes
      .filter((shape) => !erasedShapeIdsSet.has(shape.id))
      .map((shape) => shape.id)

    return undefined
  }
}



// WEBPACK FOOTER //
// ./src/modules/whiteboard/sessions/EraseSession/EraseSession.ts